/* Zaroori Jini OMS — Security page. */
import {
  ShieldCheck, Lock, KeyRound, FileText, Radio, Layers, Database,
  UserCog, AlarmClock, Cpu, Cloud, Bug,
} from "lucide-react";
import {
  RED, INK, MUTED, CREAM, BORDER, Chip, Section, MarketingShell, SectionTitle,
} from "@/components/marketing/atoms";
import { LegalHero } from "@/pages/Privacy";

const PILLARS = [
  { icon: <Lock />,     title: "Encryption",           desc: "TLS 1.2+ in transit and AES-256 at rest for all customer data, backups, and credentials." },
  { icon: <Layers />,   title: "Tenant Isolation",     desc: "Every API call is scoped to a tenant. No cross-tenant queries or joins are permitted." },
  { icon: <KeyRound />, title: "Secrets Management",   desc: "Marketplace, courier, and ERP tokens are encrypted at rest and rotated per policy." },
  { icon: <UserCog />,  title: "Role-Based Access",    desc: "Granular RBAC across users, warehouses, marketplaces, and modules." },
  { icon: <FileText />, title: "Audit Logs",           desc: "Every configuration & data change is captured in an immutable audit trail." },
  { icon: <Radio />,    title: "Continuous Monitoring",desc: "24×7 monitoring of infrastructure, application, and integration health." },
  { icon: <Database />, title: "Backups & DR",         desc: "35-day rolling backups with tested point-in-time recovery and disaster recovery drills." },
  { icon: <AlarmClock />,title:"Incident Response",    desc: "24/7 on-call rotation with published SLAs, RCAs, and postmortems." },
  { icon: <Cpu />,      title: "Least Privilege",      desc: "Zero standing production access. Break-glass access is logged and time-boxed." },
  { icon: <Cloud />,    title: "Cloud Security",       desc: "Hardened cloud posture — private VPCs, WAF, DDoS protection, secure image baselines." },
  { icon: <Bug />,      title: "Vulnerability Mgmt",   desc: "Continuous SCA, SAST, and periodic third-party penetration tests." },
  { icon: <ShieldCheck />,title:"Compliance-Ready",    desc: "SOC-ready architecture; DPDP-aligned; GDPR-friendly data processing controls." },
];

export default function Security() {
  return (
    <MarketingShell testid="security-page">
      <LegalHero eyebrow="Trust & Security" title="Security at Zaroori Jini OMS" updated="Last updated 1 Feb 2026" />

      <Section style={{ background: "#fff", paddingTop: 40 }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center", marginBottom: 40 }}>
          <p style={{ color: MUTED, fontSize: 16, lineHeight: 1.65 }}>
            We treat your operational data — orders, inventory, credentials, and
            customers — with the same seriousness as your bank does its ledger.
            Security is engineered into every module, not bolted on.
          </p>
        </div>
        <div
          className="security-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}
        >
          {PILLARS.map((p) => (
            <div
              key={p.title}
              data-testid={`security-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              style={{
                background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 12,
                padding: 22,
              }}
            >
              <div
                style={{
                  width: 40, height: 40, borderRadius: 10, background: "#fff",
                  color: RED, display: "grid", placeItems: "center",
                  border: `1px solid ${BORDER}`, marginBottom: 14,
                }}
              >
                {p.icon}
              </div>
              <div style={{ fontWeight: 800, fontSize: 15, color: INK }}>{p.title}</div>
              <div style={{ fontSize: 13, color: MUTED, marginTop: 6, lineHeight: 1.6 }}>{p.desc}</div>
            </div>
          ))}
        </div>
        <style>{`
          @media (max-width: 960px) { .security-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          @media (max-width: 560px) { .security-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </Section>

      <Section style={{ background: CREAM }}>
        <SectionTitle
          eyebrow="Reporting"
          title="Responsible Disclosure"
          description="We welcome coordinated disclosure from security researchers. Vulnerabilities can be reported confidentially and will be acknowledged within 24 hours."
        />
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center", color: INK }}>
          <div style={{ fontSize: 15, color: MUTED, lineHeight: 1.7 }}>
            Email: <b>security@zaroorijini.com</b><br />
            Please include reproduction steps, impact, and any proof of concept.
            We do not initiate legal action against good-faith researchers who follow this policy.
          </div>
        </div>
      </Section>
    </MarketingShell>
  );
}
